import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useI18n } from "../i18n/I18nContext";
import { useProfile } from "../context/ProfileContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

function nowLocalISO() {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

function toLocal(iso) {
  const d = new Date(iso);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

export default function GlucoseForm({ onSaved, record }) {
  const { t } = useI18n();
  const { activeProfileId, activeProfile } = useProfile();
  const [value, setValue] = useState(record?.value ?? "");
  const [unit, setUnit] = useState(record?.unit || activeProfile?.glucose_unit || "mg/dL");
  const [dt, setDt] = useState(record ? toLocal(record.datetime) : nowLocalISO());
  const [source, setSource] = useState(record?.source || "sensor");
  const [relation, setRelation] = useState(record?.meal_relation || "none");
  const [notes, setNotes] = useState(record?.notes || "");
  const [saving, setSaving] = useState(false);

  async function save() {
    const v = parseFloat(String(value).replace(",", "."));
    if (Number.isNaN(v) || v <= 0) {
      toast.error("Valor inválido.");
      return;
    }
    setSaving(true);
    try {
      const body = {
        value: v, unit, datetime: new Date(dt).toISOString(), source,
        meal_relation: relation === "none" ? null : relation, notes,
      };
      if (record) await api.patch(`/profiles/${activeProfileId}/glucose/${record.id}`, body);
      else await api.post(`/profiles/${activeProfileId}/glucose`, body);
      toast.success(record ? t("record_updated") : t("g_saved"));
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-4" data-testid="glucose-form">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-[#627D98]">{t("g_value")}</Label>
          <Input inputMode="decimal" value={value} onChange={(e) => setValue(e.target.value)} autoFocus data-testid="glucose-value" />
        </div>
        <div>
          <Label className="text-xs text-[#627D98]">{t("g_unit")}</Label>
          <Select value={unit} onValueChange={setUnit}>
            <SelectTrigger data-testid="glucose-unit"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="mg/dL">mg/dL</SelectItem>
              <SelectItem value="mmol/L">mmol/L</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label className="text-xs text-[#627D98]">{t("g_datetime")}</Label>
        <Input type="datetime-local" value={dt} onChange={(e) => setDt(e.target.value)} data-testid="glucose-datetime" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-[#627D98]">{t("g_source")}</Label>
          <Select value={source} onValueChange={setSource}>
            <SelectTrigger data-testid="glucose-source"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="capillary">{t("src_capillary")}</SelectItem>
              <SelectItem value="sensor">{t("src_sensor")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-[#627D98]">{t("g_relation")} ({t("optional")})</Label>
          <Select value={relation} onValueChange={setRelation}>
            <SelectTrigger data-testid="glucose-relation"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">—</SelectItem>
              <SelectItem value="fasting">Jejum</SelectItem>
              <SelectItem value="before_meal">Antes da refeição</SelectItem>
              <SelectItem value="after_meal">Depois da refeição</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label className="text-xs text-[#627D98]">{t("g_notes")} ({t("optional")})</Label>
        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="glucose-notes" />
      </div>
      <p className="text-xs text-[#627D98]">{t("manual_note")}</p>
      <Button onClick={save} disabled={saving} className="w-full h-12 bg-[#1A73E8] hover:bg-[#1557B0] text-white font-semibold" data-testid="glucose-save">
        {saving ? t("loading") : t("save")}
      </Button>
    </div>
  );
}
